import { useCallback, useEffect, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #387 — the emergency manual-ride review queue: every org ride still flagged
// needsReview (score-api GET /ride?needsReview=true, #388). Org-wide, not
// show-scoped — a manual entry can happen on any show/day. Once an admin
// resolves one (needsReview -> false server-side, #388), it naturally drops
// out of this list on the next refetch — no client-side hiding needed.
//
// #2121 — AdminShell mounts this on the VERY FIRST authenticated screen
// alongside the show load, so the class resolution below must not fan out
// one GET /class/:classId per distinct class (that used to run a
// Promise.all of N requests in the same breath as the show load). Instead
// it derives the distinct showIds the queue's rides belong to and makes ONE
// POST /class/batch call (score-api's listClassesForShows, #1961), already
// bounded server-side by MAX_BATCH_IDS + queryEachInChunks (#2024) — reusing
// that landed machinery rather than adding a client-side cap of our own.
// Mirrors scribe-fe's useScribeAssignments (#1961), which batches the exact
// same way.
//
// #2127 (judge follow-up on #2121) — showId is NOT a safe key for every
// review-queue row. createManualRide (score-api/src/handlers/ride.js
// ~L818, card #1286) deliberately creates emergency/manual rides with no
// showId at all — its own comment: a showId-less ride "is still scorable
// and still lands in the review queue" (it's Scan-based there, not
// showId-keyed). Pre-#1286 rides are the same. Those rides' classId would
// never appear in the showId-batched call above, so any ride missing from
// the batch result — showId-less rows plus the (rare) genuine gap of a
// showId whose batch response didn't carry the class — gets a per-miss
// GET /class/:classId fallback below. Bounded to just the misses, not the
// full per-row fan-out this hook used to do for EVERY row: emergency-add is
// a hard requirement (docs/process — "off-daysheet, offline"), so those rows
// must still resolve a class label and stay findable by #869's search, even
// though they can't ride the showId batch.
export function useReviewQueue() {
  const scoreApi = useScoreApi();
  const [state, setState] = useState({ rides: [], loading: true, error: null });

  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const rides = unwrap(
        await scoreApi.rides.listRides(undefined, undefined, undefined, undefined, true),
      );
      // #864 — resolve full class objects (not just a name) so the queue can
      // render classNumberLabel() via ClassLink; a row whose class never
      // resolves degrades to ReviewQueue.jsx's ClassLink `fallback` prop
      // (t("common.unknownClass"), i.e. "Unknown class" text) — not a bare
      // classId link; the classId only shows up in the row's tooltip/href.
      const showIds = [...new Set(rides.map((r) => r.showId).filter(Boolean))];
      let classById = {};
      if (showIds.length) {
        try {
          const classes = unwrap(await scoreApi.classes.listClassesForShows({ showIds }));
          classById = Object.fromEntries(classes.map((c) => [c.id, c]));
        } catch {
          // A failed batch lookup leaves classById empty here; the per-miss
          // fallback below then tries every row's classId individually.
          classById = {};
        }
      }
      // #2127 — resolve whatever the batch above didn't cover: showId-less
      // rides (never sent), plus any classId a partial/failed batch missed.
      const missingClassIds = [
        ...new Set(rides.map((r) => r.classId).filter((id) => id && !classById[id])),
      ];
      if (missingClassIds.length) {
        const entries = await Promise.all(
          missingClassIds.map((id) =>
            scoreApi.classes
              .getClass(id)
              .then(unwrap)
              .then((c) => [id, c])
              .catch(() => [id, null]),
          ),
        );
        for (const [id, c] of entries) {
          if (c) classById[id] = c;
        }
      }
      setState({
        rides: rides.map((r) => ({ ...r, class: classById[r.classId] ?? null })),
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({ rides: [], loading: false, error: err });
    }
  }, [scoreApi]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...state, refetch };
}

export default useReviewQueue;
